import { InferType, boolean, object, string, ValidationError } from 'yup';

const EmailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/; // 서버와 이메일 포맷팅 맞추기 위해서 yup 에서 제공하는 email() 사용하지 않음
const EnRegex = /[a-zA-Z]/;
const NumberRegex = /[0-9]/;
const SpecialRegex = /[~!@#$%^&*()_+`\-={}[\]|\\:";'<>?,./]/;

export const emailSchema = string()
  .trim()
  .min(4, 'Invalid email address.')
  .max(256, 'Invalid email address.')
  .matches(EmailRegex, 'Invalid email address.')
  .required('Invalid email address.');

export const passwordSchema = string()
  .trim()
  .min(8, 'min')
  .matches(EnRegex, 'en_char')
  .matches(NumberRegex, 'number_char')
  .matches(SpecialRegex, 'special_char')
  .required();

export const passwordConfirmSchema = (key: string) =>
  string().when(key, ([password], schema) => {
    return password ? schema.oneOf([password], 'Passwords do not match.') : schema;
  });

export const mfaValidator = object({
  overrideSession: boolean().required(),
  mfaSessionToken: string(),
  sms: string().length(6).required(),
  email: string().length(6).required(),
  isVerified: object({
    sms: boolean().isTrue().required(),
    email: boolean().isTrue().required(),
  }).required(),
});

export type MfaSchema = InferType<typeof mfaValidator>;

export const otpValidator = object({
  totp: string().length(6).required(),
  mfaSessionToken: string(),
});

export type OtpSchema = InferType<typeof otpValidator>;

export const emailVerifyValidator = object({
  overrideSession: boolean().required(),
  mfaSessionToken: string(),
  email: string().length(6).required(),
  isVerifiedEmail: boolean().isTrue().required(),
});

export type EmailVeritySchema = InferType<typeof emailVerifyValidator>;

export const smsVerifyValidator = object({
  overrideSession: boolean().required(),
  mfaSessionToken: string(),
  sms: string().length(6).required(),
  isVerifiedSms: boolean().isTrue().required(),
});

export type SmsVeritySchema = InferType<typeof smsVerifyValidator>;

export const addPhoneNumberValidator = object({
  phoneNumber: string().required(),
  sms: string().length(6).required(),
  isVerified: boolean().isTrue().required(),
  smsSessionToken: string().required(),
});

export type AddPhoneNumberSchema = InferType<typeof addPhoneNumberValidator>;
export const otpEmailValidator = object({
  overrideSession: boolean().required(),
  mfaSessionToken: string(),
  totp: string().length(6).required(),
  email: string().length(6).required(),
  isVerified: object({
    totp: boolean().isTrue().required(),
    email: boolean().isTrue().required(),
  }).required(),
});

export type OtpEmailSchema = InferType<typeof otpEmailValidator>;

export const geetestValidator = object({
  lotNumber: string(),
  passToken: string(),
  genTime: string(),
  captchaOutput: string(),
  deviceType: string<'WEBSITE'>().oneOf(['WEBSITE']),
}).notRequired();

export const loginValidator = object({
  email: emailSchema,
  password: string().trim().min(4).trim().required(),
  geetestCaptchaResponse: geetestValidator,
});

export type LoginSchema = InferType<typeof loginValidator>;

export const signUpValidator = object({
  email: emailSchema,
});

export type SignUpSchema = InferType<typeof signUpValidator>;

export const emailSignUpValidator = object({
  email: emailSchema.required(),
  password: passwordSchema.required(),
  passwordConfirmation: passwordConfirmSchema('password').required(),
  referralCode: string()
    .trim()
    .transform((v) => v || undefined)
    .optional(),
  agreement: boolean().oneOf([true]).required(),
  marketingConsent: boolean().optional(),
  geetestCaptchaResponse: geetestValidator,
});

export type EmailSignUpSchema = InferType<typeof emailSignUpValidator>;

export const socialSignUpValidator = object({
  token: string().required(),
  referralCode: string()
    .trim()
    .transform((v) => v || undefined)
    .optional(),
  geetestCaptchaResponse: geetestValidator,
  agreement: boolean().oneOf([true]).required(),
  marketingConsent: boolean().optional(),
});

export type SocialSignUpSchema = InferType<typeof socialSignUpValidator>;

export const forgotPasswordValidator = object({
  email: emailSchema,
  geetestCaptchaResponse: geetestValidator,
});

export type ForgotPasswordSchema = InferType<typeof forgotPasswordValidator>;

export const resetPasswordValidator = object({
  email: string().trim().min(4).required(),
  resetToken: string().required(),
  newPassword: passwordSchema,
  newPasswordConfirmation: passwordConfirmSchema('newPassword'),
});

export type ResetPasswordSchema = InferType<typeof resetPasswordValidator>;

export const changePwValidator = object({
  oldPassword: string().trim().required().label('Current Password'),
  newPassword: passwordSchema,
  newPasswordConfirmation: passwordConfirmSchema('newPassword'),
  requiredMfa: boolean().default(false),
  mfaSessionToken: string().when('requiredMfa', {
    is: true,
    then: (schema) => schema.required(),
  }),
});

export type ChangePwSchema = InferType<typeof changePwValidator>;

export const checkPasswordValidStatus = (password: string = '') => {
  try {
    passwordSchema.validateSync(password, { abortEarly: false });
    return {
      isValidLength: true,
      isValidLetter: true,
      isValidNumber: true,
      isValidSpecial: true,
    };
  } catch (err) {
    const { errors } = err as ValidationError;
    return {
      isValidLength: !errors.includes('min'),
      isValidLetter: !errors.includes('en_char'),
      isValidNumber: !errors.includes('number_char'),
      isValidSpecial: !errors.includes('special_char'),
    };
  }
};
